// Models
import { RecurrencePatternType } from "../../../../service/planner/enumerations/IPlannerEnumerations";
import { DateWithOptionalTime } from "../../common/DateWithOptionalTime";
// Utilities
import { Temporal } from "temporal-polyfill-lite";
import isEmpty from "lodash/isEmpty";
import { DailyRecurrenceCalculator } from "../calculator/DailyRecurrenceCalculator";
import { MonthlyRecurrenceCalculator } from "../calculator/MonthlyRecurrenceCalculator";
import { WeeklyRecurrenceCalculator } from "../calculator/WeeklyRecurrenceCalculator";
import { YearlyRecurrenceCalculator } from "../calculator/YearlyRecurrenceCalculator";
import { getDaysOrDates } from "./DayOrDateUtilities";
/**
 * Maximum number of intervals to check when calculating due dates or next occurrence dates.
 * Choosing a small number to avoid performance issues in case of malformed patterns.
 * At most the loop should find a date within 2 iterations, but we give some buffer.
 */ const MAX_INTERVALS_TO_CHECK = 4;
/**
 * Calculate the first date on or after the {@link minDueDate} that fulfills the {@link schedule}.
 * @param schedule Schedule of the recurrence.
 * @param minDueDate Minimum due date for the schedule.
 * @returns The first date on or after the {@link minDueDate} that satisfies the {@link schedule}.
 *
 * @description
 * This differs from {@link calculateNextOccurrenceDateFromSchedule} in that the first date is going to follow
 * the {@link schedule} and up to at most 1 period later.
 */ export function calculateDueDateFromSchedule(schedule, minDueDate) {
    const daysOrDates = getDaysOrDates(schedule.pattern);
    if (daysOrDates == null || isEmpty(daysOrDates)) {
        // For daily or empty patterns, just add the interval to the starting date.
        // Assuming daily or empty patterns mean every day.
        return minDueDate.getClone();
    }
    // We should find a due date within the first two periods, so we start with interval 0 and go up,
    // terminating after finding at least one date.
    let results = [];
    let currentInterval = 0;
    do {
        results = daysOrDates.map((dayOrDate)=>dayOrDate.resolveDateForPeriod(minDueDate.date.add({
                [getPatternDiff(schedule.pattern.patternType)]: currentInterval
            }))).filter((date)=>Temporal.PlainDate.compare(date, minDueDate.date) >= 0).sort((a, b)=>Temporal.PlainDate.compare(a, b) < 0 ? -1 : 1);
        currentInterval++;
    }while (results.length === 0 && currentInterval < MAX_INTERVALS_TO_CHECK)
    if (results.length === 0) {
        throw new Error("Could not calculate due date from schedule.");
    }
    return DateWithOptionalTime.fromPlainDate(results[0]);
}
/**
 * Calculate the next date after the {@link startingDate} that fulfills the {@link pattern}.
 *
 * @param pattern Pattern of the recurrence.
 * @param startingDate Starting date for the pattern.
 * @returns The next date following the {@link startingDate} that satisfies the {@link pattern}.
 *
 * @description
 * This differs from {@link calculateDueDateFromSchedule} in that the next date is going to follow the
 * {@link pattern} and the patterns pattern interval periods later.
 */ export function calculateNextOccurrenceDateFromSchedule(pattern, startingDate) {
    const daysOrDates = getDaysOrDates(pattern);
    if (daysOrDates == null || isEmpty(daysOrDates)) {
        // For daily or empty patterns, just add the interval to the starting date.
        // Assuming daily or empty patterns mean every day.
        return startingDate.add({
            days: pattern.interval
        });
    }
    // We should find the next occurrence after startingDate, so we start with interval 0 and go up,
    // terminating after finding at least one date.
    let results = [];
    let currentInterval = 0;
    do {
        results = daysOrDates.map((dayOrDate)=>dayOrDate.resolveDateForPeriod(startingDate.add({
                [getPatternDiff(pattern.patternType)]: currentInterval
            }))).filter((date)=>Temporal.PlainDate.compare(date, startingDate) > 0).sort((a, b)=>Temporal.PlainDate.compare(a, b) < 0 ? -1 : 1);
        currentInterval += pattern.interval;
    }while (results.length === 0 && currentInterval < MAX_INTERVALS_TO_CHECK)
    if (results.length === 0) {
        throw new Error("Could not calculate next occurrence date from schedule.");
    }
    return results[0];
}
/**
 * Get the Temporal.Duration key for the pattern type.
 * @param patternType Pattern type of the recurrence.
 */ function getPatternDiff(patternType) {
    switch(patternType){
        case RecurrencePatternType.Weekly:
            return "weeks";
        case RecurrencePatternType.FixedDayOfMonth:
        case RecurrencePatternType.RelativeDayOfMonth:
            return "months";
        case RecurrencePatternType.FixedDayOfYear:
        case RecurrencePatternType.RelativeDayOfYear:
            return "years";
        case RecurrencePatternType.Daily:
        default:
            return "days";
    }
}
/**
 * Get the Recurrence Calculator representing the recurrence pattern on a recurring task.
 * @param task A task to create the calculator from.
 * @param nextOccurrenceDate Recurrence start date.
 * @returns The calculator needed to represent the pattern.
 *
 * @remarks If the {@link task} is not recurring null will be returned.
 */ export function getRecurrenceCalculatorForTask(task) {
    if (task.dueDateTime == null || !task.recurrence?.isActive()) {
        return null;
    }
    /*
     * Date used by the calculator should be nextOccurrenceDate. The calculator bases all logic around this
     * assumption. However, nextOccurrenceDate may not exist for optimistically created recurrences. We need
     * to calculate the nextOccurrenceDate. To calculate this we can use the dueDate on the current occurrence
     * which satisfies the recurrence pattern. This assumption that dueDate satisifies the recurrence pattern
     * is false for all but optimistically created recurrences. This is because when we create a recurrence on
     * a task we calculate the first date that satisfies the recurrence and set that date as the dueDate.
     * After the recurrence is created on the service, the nextOccurrenceDate will be supplied. After this
     * the dueDate may not satisfy the recurrence as they are not tightly coupled.
     */ const nextOccurrenceDate = task.recurrence.schedule.nextOccurrenceDateTime ?? calculateNextOccurrenceDateFromSchedule(task.recurrence.schedule.pattern, task.dueDateTime.date).toZonedDateTime("UTC").toInstant();
    // Depending on the recurrence pattern type, return the appropriate calculator
    switch(task.recurrence.schedule.pattern.patternType){
        case RecurrencePatternType.Weekly:
            return new WeeklyRecurrenceCalculator(task.recurrence.schedule, nextOccurrenceDate, task.recurrence.occurrenceId);
        case RecurrencePatternType.FixedDayOfMonth:
        case RecurrencePatternType.RelativeDayOfMonth:
            return new MonthlyRecurrenceCalculator(task.recurrence.schedule, nextOccurrenceDate, task.recurrence.occurrenceId);
        case RecurrencePatternType.FixedDayOfYear:
        case RecurrencePatternType.RelativeDayOfYear:
            return new YearlyRecurrenceCalculator(task.recurrence.schedule, nextOccurrenceDate, task.recurrence.occurrenceId);
        case RecurrencePatternType.Daily:
        default:
            return new DailyRecurrenceCalculator(task.recurrence.schedule, nextOccurrenceDate, task.recurrence.occurrenceId);
    }
}
